<script setup lang="ts">
import { computed, type Component } from 'vue';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import RecordLink from '@/components/RecordLink.vue';
import PasswordValue from '@/components/record/fields/PasswordValue.vue';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { buildRowSummaryParts, type SummaryPart } from '@/utils/searchRowSummary';
import type { TableColumn } from '@/types/tableTypes';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { hiddenMatch, useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';

/**
 * Compact master-list row for the search page's Apple Mail-style cards view.
 * The header (entity-specific via slot) is fixed; below it a summary of the
 * user's visible columns — every value prints in full (wrapping, never
 * clamped), so Column Settings alone decides how much each row shows. Foreign-model values in the summary
 * link to the related record's page, with a side-peek button beside each one
 * (see RecordLink).
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        titleIcon?: Component | null;
        visibleColumns: TableColumn[];
        excludeFields?: string[];
        /** Open in the inspector (master-detail "current" row). */
        active?: boolean;
        /** Multi-select membership (shift-range / cmd-toggle): tints the card background. */
        selected?: boolean;
        /**
         * Draw a checkbox before the card's content.
         * Ticking it emits `toggle-select` and never opens the record.
         */
        selectable?: boolean;
        /** Live polling no longer finds this row (deleted / de-scoped): dim it, keep last-known values. */
        missing?: boolean;
        /**
         * Quick-search match keys the header slot draws for this row, from
         * `cardShownMatchFields`. A match on one of them is highlighted in the
         * header, so the card adds no extra line for it.
         */
        shownFieldKeys?: readonly string[];
    }>(),
    { excludeFields: () => [], active: false, selected: false, selectable: false, missing: false, shownFieldKeys: () => [] }
);

const emit = defineEmits<{
    (e: 'select', row: Record<string, unknown>, event?: MouseEvent): void;
    /** The checkbox was clicked. The event carries the shift key, so the page can tick a range. */
    (e: 'toggle-select', row: Record<string, unknown>, event: MouseEvent): void;
}>();

const summaryParts = computed(() =>
    buildRowSummaryParts(props.row, props.visibleColumns, props.excludeFields)
);

/**
 * Quick search (SAU-467): the card highlights the values of the fields this record
 * matched, and adds one line for a matched field it does not show. Outside a searched
 * list there is no context, and nothing is highlighted.
 */
const quickSearch = useQuickSearchMatches();
/** The fields the default header's title reads from. */
const TITLE_FIELD_KEYS = ['display_string', 'name'];
const matchedFieldKeys = computed(() => new Set((quickSearch?.matchesFor(props.row.id) ?? []).map((match) => match.field)));
const columnKeyById = computed(() => new Map(props.visibleColumns.map((column) => [column.id, column.key])));
const titleMatched = computed(() => TITLE_FIELD_KEYS.some((key) => matchedFieldKeys.value.has(key)));
function partMatched(part: SummaryPart): boolean {
    const key = columnKeyById.value.get(part.key);
    return key !== undefined && matchedFieldKeys.value.has(key);
}
const cardHiddenMatch = computed(() => {
    if (!quickSearch) return null;
    const shown = new Set([
        ...TITLE_FIELD_KEYS,
        ...props.shownFieldKeys,
        ...summaryParts.value.map((part) => columnKeyById.value.get(part.key) ?? part.key),
    ]);
    return hiddenMatch(quickSearch.matchesFor(props.row.id), shown);
});
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);

/** Only link relations whose entity models.json knows (inspector columns derivable). */
const { canOpen } = useOpenRecordInInspector();

/** The row's id. A password shown on one card stays masked on every other card. */
const recordKey = computed(() => props.row.id as string | number | undefined);

function isInspectableRelation(part: SummaryPart): boolean {
    return part.relation !== undefined && canOpen(part.relation.entityId);
}

const titleText = computed(() => {
    const display = props.row.display_string ?? props.row.name;
    if (typeof display === 'string' && display.trim() !== '') {
        return display;
    }
    return `#${props.row.id}`;
});

function onClick(event: MouseEvent) {
    emit('select', props.row, event);
}

function onToggleSelect(event: MouseEvent) {
    emit('toggle-select', props.row, event);
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        emit('select', props.row, event as unknown as MouseEvent);
    }
}
</script>

<template>
    <!-- The card fills on hover, except while the pointer is over one of a custom list card's
         schedule lines. That line fills instead, so only one thing reads as highlighted. -->
    <Card
        class="group hover:border-border w-full cursor-pointer gap-0 border-border/80 p-3 shadow-none transition-colors"
        :class="[
            active
                ? 'border-primary bg-muted/50 ring-1 ring-primary/40'
                : selected
                    ? 'bg-muted/40'
                    : 'hover:not-has-[[data-card-schedule-line]:hover]:bg-muted/30',
            missing ? 'opacity-45' : '',
        ]"
        role="button"
        tabindex="0"
        data-testid="search-result-list-row"
        :data-active="active ? 'true' : 'false'"
        :data-selected="selected ? 'true' : 'false'"
        :data-live-poll-missing="missing ? 'true' : undefined"
        :title="missing ? 'No longer found — run the search again to refresh this list.' : undefined"
        :aria-pressed="active ? 'true' : 'false'"
        @click="onClick"
        @keydown="onKeydown"
    >
        <div class="flex items-start gap-2.5">
            <!-- The checkbox sits beside the content, not in the header slot.
                 A container card's header is a three-column grid, and a fourth
                 child there would break its rows. The card itself opens the
                 record on click and on Enter or Space, so the checkbox stops
                 both from reaching it. -->
            <!-- It shows while the pointer is over the card, while it has keyboard focus, or once
                 ticked. It keeps its space when hidden, so the card never shifts as it appears.
                 A device with no hover, such as a phone, always shows it.
                 A custom list card draws a checkbox on each schedule line too. With the pointer over
                 one of those lines, this checkbox hides, so only the line's checkbox shows. -->
            <Checkbox
                v-if="selectable"
                class="mt-0.5 shrink-0 transition-opacity"
                :class="selected ? '' : 'opacity-0 group-hover:not-group-has-[[data-card-schedule-line]:hover]:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100'"
                :model-value="selected"
                :aria-label="`Select ${titleText}`"
                data-testid="search-result-list-row-checkbox"
                @click.stop="onToggleSelect"
                @keydown.stop
            />
            <div class="min-w-0 flex-1">
                <!-- Optional floated lead (e.g. the Live Track cover thumb):
                     rendered inside the content flow so the header sits beside
                     it and the summary text wraps around it magazine-style,
                     reclaiming the full width below the float. -->
                <slot name="leading" />
                <slot name="header">
                    <div class="flex items-center gap-2">
                        <component v-if="titleIcon" :is="titleIcon" class="size-4 shrink-0" aria-hidden="true" />
                        <h3 class="min-w-0 flex-1 truncate text-sm font-semibold leading-tight" :title="titleText">
                            <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="titleMatched">{{ titleText }}</SearchHighlight>
                        </h3>
                        <!-- Labelled, because a bare number in a card corner
                             reads as a position in the list rather than the
                             record's id. -->
                        <span class="shrink-0 font-mono text-xs tabular-nums whitespace-nowrap">
                            ID {{ row.id }}
                        </span>
                    </div>
                </slot>
                <!-- Fixed-position block between the header and the dynamic
                     column summary (e.g. the LZP Currently Playing block). -->
                <div v-if="$slots.subheader">
                    <slot name="subheader" />
                </div>
                <p
                    v-if="summaryParts.length > 0"
                    class="text-muted-foreground mt-1 text-xs leading-snug"
                    data-testid="search-result-list-row-summary"
                >
                    <template v-for="(part, index) in summaryParts" :key="part.key">
                        <template v-if="index > 0">{{ ' · ' }}</template>
                        <!-- The related record's name links to its page, with the side-peek
                             button on hover. RecordLink stops the click, so following the
                             link never also selects this card. In `inline` mode its wrapper
                             is a plain inline box, so the dotted underline carries onto the link. -->
                        <template v-if="isInspectableRelation(part)"
                            >{{ part.header }}
                            <RecordLink
                                :entity-id="part.relation!.entityId"
                                :record-id="part.relation!.recordId"
                                :label="`${part.relation!.entityId} ${part.text}`"
                                inline
                                class="hover:text-foreground underline decoration-dotted underline-offset-2"
                                :data-testid="`summary-relation-${part.key}`"
                                @keydown.stop
                                >{{ part.text }}</RecordLink
                            ></template
                        >
                        <!-- A login password shows masked, with a button that shows it. The
                             button stops its click, so it never also selects this card. -->
                        <template v-else-if="part.password !== undefined"
                            >{{ part.header }}
                            <PasswordValue
                                class="align-middle"
                                :value="part.password"
                                :label="part.header"
                                :record-key="recordKey"
                        /></template>
                        <template v-else
                            >{{ part.header }}
                            <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="partMatched(part)">{{
                                part.text
                            }}</SearchHighlight></template
                        >
                    </template>
                </p>
                <!-- A matched field the card does not show, such as a player's public IP:
                     its value, highlighted, and no label. It wraps rather than clips,
                     so a long value never pushes the match out of sight. -->
                <p
                    v-if="cardHiddenMatch"
                    class="mt-1 wrap-anywhere text-xs text-muted-foreground"
                    data-testid="search-result-list-row-hidden-match"
                >
                    <SearchHighlight
                        :name="LIST_HIGHLIGHT"
                        :terms="highlightTerms"
                        :whole="quickSearch?.isNumberField(cardHiddenMatch.field) ?? false"
                    >{{ cardHiddenMatch.value }}</SearchHighlight>
                </p>
                <div>
                    <slot name="footer" />
                </div>
            </div>
        </div>
    </Card>
</template>
